import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { dispatch } from "../../../state/store";
import { setGroupToSelectedApp } from "../../../state/ducks";

import { alignItems, colors, strCode } from "../../../consts";
import { appsService, groupsService, log, notificationService, t } from "../../../services";
import { responseUtil } from "../../../utils";

import {
  FlexContainers,
  Heading5,
  Heading6,
  Input,
  Modal,
  Paragraph2,
  SmallButton,
  TextButton,
  WrapperContainer
} from "../../../components";
import RolesFeaturesTable from "./RolesFeaturesTable";
import { LabelText } from "./styled";

const RolesTabModal = ({
  isActive,
  onModalClose,
  roleAdditionFlag,
  role,
  setRole,
  isReadOnlyAccess
}) => {
  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const groupConfigs = useSelector((store) => store.apps.selectedApp.groupConfigs);
  const defaultFeatures = useSelector((store) => store.apps.selectedApp.defaultSettings);

  const [showConfirm, setShowConfirm] = useState(false);
  const [roleFeatures, setRoleFeatures] = useState([]);
  const [originalRoleName, setOriginalRoleName] = useState("");
  const [originalRoleFeatures, setOriginalRoleFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!roleFeatures.length && isActive) {
      if (!groupConfigs.length || !defaultFeatures.length) {
        setRoleFeatures([]);
        setOriginalRoleFeatures([]);
      }

      const selectedGroup = groupConfigs.find((config) => config.group === role);

      const features = defaultFeatures.map((feature) => {
        return {
          ...feature,
          value: role ? selectedGroup?.features.includes(feature.code) : feature.value
        };
      });

      setRoleFeatures(features);
      setOriginalRoleFeatures(features);
    }
  }, [defaultFeatures, groupConfigs, role, isActive]);

  useEffect(() => {
    if (!originalRoleName) {
      setOriginalRoleName(role);
    }
  }, [role]);

  useEffect(() => {
    if (!isActive) {
      setRoleFeatures([]);
      setOriginalRoleName("");
    }
  }, [isActive]);

  const handleAppNameChange = (value) => {
    setRole(value);
  };

  const saveGroup = useCallback(async () => {
    if (isLoading) return;

    let data;

    const newGroup = {
      appType: selectedApp.appType,
      group: originalRoleName,
      features: roleFeatures.filter((feature) => feature.value).map((feature) => feature.code)
    };

    const isFeaturesChanged =
      JSON.stringify(
        roleFeatures.filter((feature) => feature.value).map((feature) => feature.code)
      ) !==
      JSON.stringify(
        originalRoleFeatures.filter((feature) => feature.value).map((feature) => feature.code)
      );

    try {
      setIsLoading(true);

      if (roleAdditionFlag) {
        await appsService.createAppRoles({ ...newGroup, group: role });

        data = await groupsService.fetchSelectedGroup(selectedApp.appType);
      } else {
        try {
          let res;

          if (isFeaturesChanged) {
            res = await appsService.updateRoles(newGroup);
          }

          if (originalRoleName !== role) {
            res = await appsService.updateRoleName({
              oldGroupName: originalRoleName,
              appType: selectedApp.appType,
              newGroupName: role
            });
          }

          if (responseUtil.isResponseSuccessful(res)) {
            notificationService.showSuccess({
              title: t(strCode.APP_UPDATED_SHORT_TITLTE),
              message: t(strCode.APP_UPDATED_LONG_TITLTE, { name: selectedApp.name })
            });
          }
        } catch (err) {
          log.error("RolesTabModal: Failed to update app role", err);
          notificationService.showError({
            title: t(strCode.APP_ROLE_UPDATE_TITLE_ERROR),
            message: err?.response?.data?.message ? err.response.data.message : err?.toString()
          });
        }

        data = await groupsService.fetchSelectedGroup(selectedApp.appType);
      }
    } finally {
      setIsLoading(false);
    }

    dispatch(setGroupToSelectedApp(data));
    setRole("");
    onModalClose();
  }, [roleAdditionFlag, selectedApp, role, roleFeatures, isLoading]);

  const onDeleteGroup = useCallback(async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);

      await appsService.deleteRole({
        appType: selectedApp.appType,
        group: role
      });
      const data = await groupsService.fetchSelectedGroup(selectedApp.appType);

      dispatch(setGroupToSelectedApp(data));
      setRole("");
      setShowConfirm(false);
      onModalClose();
    } catch (e) {
      return e;
    } finally {
      setIsLoading(false);
    }
  }, [selectedApp, isLoading, role]);

  const onToggleFeature = (featureName) => {
    setRoleFeatures(
      roleFeatures.map((feature) => {
        if (feature.settingName === featureName) {
          return {
            ...feature,
            value: !feature.value
          };
        }

        return feature;
      })
    );
  };

  return showConfirm ? (
    <Modal id="roles-tab-modal" active={isActive} closable={false} preventOverflow>
      <WrapperContainer Container={FlexContainers.FlexColumnStart} width={380} alignItems="start">
        <Heading5 style={{ marginBottom: "16px" }}>
          {t(strCode.REMOVE_ROLE_TITLE, { role: originalRoleName })}
        </Heading5>

        <Paragraph2 style={{ marginBottom: "24px" }}>
          {t(strCode.REMOVE_ROLE_WARNING_TITLE, { role: originalRoleName })}
        </Paragraph2>

        <WrapperContainer width={"100%"} gap={16} Container={FlexContainers.FlexRowEnd}>
          <SmallButton onClick={() => setShowConfirm(false)} disabled={isLoading} secondary={true}>
            {t(strCode.CANCEL_BTN)}
          </SmallButton>
          <SmallButton
            onClick={onDeleteGroup}
            disabled={isLoading}
            loading={isLoading}
            style={{ backgroundColor: colors.ERROR }}>
            {t(strCode.DELETE_BTN)}
          </SmallButton>
        </WrapperContainer>
      </WrapperContainer>
    </Modal>
  ) : (
    <Modal
      id="roles-tab-modal"
      active={isActive}
      closable={false}
      modalHeader={
        <Heading6>
          {!roleAdditionFlag
            ? t(strCode.EDIT_ROLE_NAME, { role: originalRoleName })
            : t(strCode.NEW_ROLE_TITLE)}
        </Heading6>
      }
      modalFooter={
        <WrapperContainer Container={FlexContainers.FlexRowBetween} width={"100%"}>
          {!isReadOnlyAccess && !roleAdditionFlag && (
            <TextButton
              onClick={() => setShowConfirm(true)}
              disabled={isLoading}
              style={{ color: colors.ERROR }}>
              {t(strCode.REMOVE_BTN)}
            </TextButton>
          )}

          <WrapperContainer Container={FlexContainers.FlexRowEnd} width="100%" gap={8}>
            <SmallButton secondary secondaryBold disabled={isLoading} onClick={onModalClose}>
              {t(strCode.CANCEL_BTN)}
            </SmallButton>

            <SmallButton disabled={!role || isLoading} loading={isLoading} onClick={saveGroup}>
              {t(strCode.SAVE_BTN)}
            </SmallButton>
          </WrapperContainer>
        </WrapperContainer>
      }
      preventOverflow>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        width={574}
        gap={24}
        alignItems="start">
        <Input
          id={"app-name"}
          title={t(strCode.ROLE_NAME_TITLE)}
          value={role}
          setValue={handleAppNameChange}
          placeholder={t(strCode.ROLE_NAME_TITLE)}
        />

        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          width={"100%"}
          gap={8}>
          <LabelText>{t(strCode.MANAGE_ROLE_FEATURE_TITLE)}</LabelText>
          <RolesFeaturesTable features={roleFeatures} onToggleValue={onToggleFeature} />
        </WrapperContainer>
      </WrapperContainer>
    </Modal>
  );
};

RolesTabModal.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  roleAdditionFlag: PropTypes.bool.isRequired,
  role: PropTypes.string.isRequired,
  setRole: PropTypes.func.isRequired,
  isReadOnlyAccess: PropTypes.bool.isRequired
};

export default RolesTabModal;
